import BaseComponentController from "../base_component_controller";
import { computePosition, offset, flip, shift, arrow, autoUpdate } from "@floating-ui/dom";
import AirDatepicker from "air-datepicker";
import localeEn from "air-datepicker/locale/en";
import localeFr from "air-datepicker/locale/fr";

export default class extends BaseComponentController {
  static values = {
    placement: { type: String, default: "bottom-start" }, // Placement of the datepicker
    range: { type: Boolean, default: false }, // Whether to allow selecting a range of dates
    disabledDates: { type: Array, default: [] }, // Expects array of 'YYYY-MM-DD' strings
    timepicker: { type: Boolean, default: false },
    timeOnly: { type: Boolean, default: false }, // New value for time-only selection
    weekPicker: { type: Boolean, default: false }, // New value for week selection
    timeFormat: { type: String, default: "" }, // Default empty, logic will apply 'hh:mm AA' if timepicker is true and this is empty
    minHours: Number, // Undefined if not set, AirDatepicker uses its default
    maxHours: Number, // Undefined if not set
    minutesStep: Number, // Undefined if not set
    showTodayButton: { type: Boolean, default: false },
    showClearButton: { type: Boolean, default: false },
    showThisMonthButton: { type: Boolean, default: false },
    showThisYearButton: { type: Boolean, default: false },
    dateFormat: { type: String, default: "" }, // e.g., 'MM/dd/yyyy', AirDatepicker default if empty
    startView: { type: String, default: "days" }, // 'days', 'months', 'years'
    minView: { type: String, default: "days" }, // 'days', 'months', 'years'
    initialDate: { type: String, default: "" }, // 'YYYY-MM-DD' or JSON array of 'YYYY-MM-DD' for range
    minDate: { type: String, default: "" }, // 'YYYY-MM-DD'
    maxDate: { type: String, default: "" }, // 'YYYY-MM-DD'
    inline: { type: Boolean, default: false }, // Makes the calendar permanently visible
    // presetClasses: { type: Object, default: {} }, // Classes for the preset buttons
    locale: { type: String }, // Locale for the datepicker
    translations: { type: Object, default: {} }, // Translations for some buttons or views
  };

  connect() {
    this.initializeDatepicker();
    this.element.addEventListener("keydown", this.handleKeydown.bind(this));
  }

  disconnect() {
    if (this.datepickerInstance) {
      this.datepickerInstance.destroy();
      this.datepickerInstance = null;
    }

    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate();
      this.cleanupAutoUpdate = null;
    }
    if (this.currentPositionCleanupAutoUpdate) {
      this.currentPositionCleanupAutoUpdate();
      this.currentPositionCleanupAutoUpdate = null;
    }

    if (this.element) {
      this.element.removeEventListener("keydown", this.handleKeydown.bind(this));
    }
  }

  initializeDatepicker() {
    if (this.datepickerInstance) {
      this.datepickerInstance.destroy();

      if (this.cleanupAutoUpdate) {
        this.cleanupAutoUpdate();
        this.cleanupAutoUpdate = null;
      }

      if (this.currentPositionCleanupAutoUpdate) {
        this.currentPositionCleanupAutoUpdate();
        this.currentPositionCleanupAutoUpdate = null;
      }
    }

    const options = this.buildDatepickerOptions();

    if (!this.inlineValue) {
      options.position = this.createPositionFunction();
    }

    this.datepickerInstance = new AirDatepicker(this.element, options);

    if (this.weekPickerValue) {
      setTimeout(() => this.updateWeekDisplay(), 0);
    }

    if (this.inlineValue) {
      setTimeout(() => this.triggerChangeEvent(), 0);
    }
  }

  buildDatepickerOptions() {
    const options = {
      locale: this.availableLocales[this.localeValue],
      autoClose: !this.inlineValue,
      inline: this.inlineValue,
      container: this.element.closest("dialog") || undefined,
    };

    if (this.timeOnlyValue) {
      options.dateFormat = "";
      options.onSelect = this.createTimeOnlySelectHandler();
    } else if (this.weekPickerValue) {
      options.dateFormat = "";
      options.onSelect = this.createWeekSelectHandler();
    } else if (this.hasDateFormatValue && this.dateFormatValue) {
      options.dateFormat = this.dateFormatValue;
    }

    if (this.hasStartViewValue) options.view = this.startViewValue;
    if (this.hasMinViewValue) options.minView = this.minViewValue;

    this.setDateConstraint(options, "minDate", this.minDateValue);
    this.setDateConstraint(options, "maxDate", this.maxDateValue);

    const initialDates = this.parseInitialDates();
    if (initialDates.length > 0) options.selectedDates = initialDates;

    if (this.rangeValue) {
      options.range = true;
      options.multipleDatesSeparator = " - ";
    }

    if (this.timepickerValue || this.timeOnlyValue) {
      Object.assign(options, {
        timepicker: true,
        timeFormat: this.timeFormatValue || this.availableTimeFormat[this.localeValue],
        ...(this.hasMinHoursValue && { minHours: this.minHoursValue }),
        ...(this.hasMaxHoursValue && { maxHours: this.maxHoursValue }),
        ...(this.hasMinutesStepValue && { minutesStep: this.minutesStepValue }),
      });
      if (this.timeOnlyValue) options.classes = "only-timepicker";
    }

    const buttons = this.buildButtons();
    if (buttons.length > 0) options.buttons = buttons;

    const disabledDates = this.parseDisabledDates();
    if (disabledDates.length > 0) {
      options.onRenderCell = this.createRenderCellHandler(disabledDates);
    }

    if (!this.timeOnlyValue && !this.weekPickerValue && this.inlineValue) {
      const originalOnSelect = options.onSelect;
      options.onSelect = (params) => {
        originalOnSelect?.(params);
        this.triggerChangeEvent();
      };
    }

    if (this.timeOnlyValue && this.inlineValue) {
      const originalOnSelect = options.onSelect;
      options.onSelect = ({ date, datepicker }) => {
        if (originalOnSelect) {
          originalOnSelect({ date, datepicker });
        }
        this.triggerChangeEvent();
      };
    }

    return options;
  }

  setDateConstraint(options, key, value) {
    if (value) {
      const date = this.parseDate(value);
      if (date) options[key] = date;
    }
  }

  parseInitialDates() {
    if (!this.hasInitialDateValue || !this.initialDateValue) return [];

    try {
      if (this.initialDateValue.startsWith("[") && this.initialDateValue.endsWith("]")) {
        const dateStrings = JSON.parse(this.initialDateValue);
        return dateStrings.map((str) => this.parseDate(str)).filter(Boolean);
      }

      const date = this.parseDate(this.initialDateValue);
      return date ? [date] : [];
    } catch (e) {
      console.error("Error parsing initialDateValue:", e, "Value was:", this.initialDateValue);
      return [];
    }
  }

  buildButtons() {
    const buttons = [];

    const buttonConfigs = [
      { condition: this.showTodayButtonValue, button: this.createTodayButton() },
      { condition: this.showThisMonthButtonValue, button: this.createMonthButton() },
      { condition: this.showThisYearButtonValue, button: this.createYearButton() },
      { condition: this.showClearButtonValue, button: this.createClearButton() },
    ];

    buttonConfigs.forEach(({ condition, button }) => {
      if (condition && button) buttons.push(button);
    });

    return buttons;
  }

  createTodayButton() {
    const isTimepickerEnabled = this.timepickerValue || this.timeOnlyValue || this.weekPickerValue;
    const buttonText = this.weekPickerValue ? this.translationsValue.thisWeek : isTimepickerEnabled ? this.translationsValue.now : this.translationsValue.today;

    return {
      content: buttonText,
      onClick: (dp) => {
        const currentDate = new Date();
        const dates = this.rangeValue ? [currentDate, currentDate] : currentDate;

        if (isTimepickerEnabled && !this.weekPickerValue) {
          dp.clear();
          setTimeout(() => dp.selectDate(dates, { updateTime: true }), 0);
        } else {
          dp.selectDate(dates);
        }
      },
    };
  }

  createMonthButton() {
    return {
      content: this.translationsValue.thisMonth,
      onClick: (dp) => {
        const currentDate = new Date();
        dp.selectDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
      }
    };
  }

  createYearButton() {
    return {
      content: this.translationsValue.thisYear,
      onClick: (dp) => {
        const currentDate = new Date();
        dp.selectDate(new Date(currentDate.getFullYear(), 0, 1));
      }
    };
  }

  createClearButton() {
    return {
      content: this.translationsValue.clear,
      onClick: (dp) => {
        dp.clear();
        this.element.value = "";
        this.triggerChangeEvent();
      }
    };
  }

  parseDisabledDates() {
    if (!this.disabledDatesValue?.length) return [];

    return this.disabledDatesValue.map((str) => this.parseDate(str)).filter(Boolean);
  }

  createRenderCellHandler(disabledDates) {
    return ({ date, cellType }) => {
      if (cellType !== "day") return {};

      const cellDateUTC = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const isDisabled = disabledDates.some((disabledDate) => disabledDate.getTime() === cellDateUTC.getTime());

      return isDisabled ? { disabled: true } : {};
    };
  }

  createTimeOnlySelectHandler() {
    return ({ date, datepicker }) => {
      if (date) {
        const timeFormat = this.timeFormatValue || this.availableTimeFormat[this.localeValue];
        this.element.value = datepicker.formatDate(date, timeFormat);
        this.triggerChangeEvent();
      }
    };
  }

  createWeekSelectHandler() {
    return ({ date }) => {
      if (date) {
        const weekNumber = this.getWeekNumber(date);
        const year = date.getFullYear();
        this.element.value = `${year}-W${weekNumber.toString().padStart(2, "0")}`;
        this.triggerChangeEvent();
      }
    };
  }

  createPositionFunction() {
    return ({ $datepicker, $target, $pointer, done }) => {
      const middleware = [offset(8), flip(), shift({ padding: 8 })];

      if ($pointer instanceof HTMLElement) {
        middleware.push(arrow({ element: $pointer, padding: 5 }));
      }

      this.cleanupPositioning();

      this.currentPositionCleanupAutoUpdate = autoUpdate(
        $target,
        $datepicker,
        () => {
          computePosition($target, $datepicker, {
            placement: this.placementValue,
            middleware: middleware,
          }).then(({ x, y, middlewareData }) => {
            Object.assign($datepicker.style, { left: `${x}px`, top: `${y}px` });

            if ($pointer instanceof HTMLElement && middlewareData.arrow) {
              const { x: arrowX, y: arrowY } = middlewareData.arrow;
              Object.assign($pointer.style, {
                left: arrowX != null ? `${arrowX}px` : "",
                top: arrowY != null ? `${arrowY}px` : "",
              });
            }
          });
        },
        { animationFrame: true }
      );

      this.cleanupAutoUpdate = this.currentPositionCleanupAutoUpdate;

      return () => {
        this.cleanupPositioning();
        done();
      };
    };
  }

  getWeekNumber(date) {
    const tempDate = new Date(date.getTime());
    const dayNumber = (tempDate.getDay() + 6) % 7;
    tempDate.setDate(tempDate.getDate() - dayNumber + 3);

    const firstThursday = new Date(tempDate.getFullYear(), 0, 4);
    firstThursday.setDate(firstThursday.getDate() - ((firstThursday.getDay() + 6) % 7) + 3);

    return Math.round((tempDate.getTime() - firstThursday.getTime()) / 86400000 / 7) + 1;
  }

  parseDate(dateString) {
    if (!dateString || typeof dateString !== "string") return null;

    // First check if it's a datetime string (YYYY-MM-DD HH:MM)
    if (dateString.includes(" ")) {
      const [datePart, timePart] = dateString.split(" ");
      const dateParts = datePart.split("-");

      if (dateParts.length === 3 && timePart) {
        const [year, month, day] = dateParts.map(Number);
        const [hours, minutes] = timePart.split(":").map(Number);

        if (!isNaN(year) && !isNaN(month) && !isNaN(day) && !isNaN(hours) && !isNaN(minutes)) {
          return new Date(year, month - 1, day, hours, minutes);
        }
      }
    }

    // Otherwise try to parse as date only (YYYY-MM-DD)
    const parts = dateString.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(Date.UTC(year, month - 1, day));
      }
    }

    console.warn(`Invalid date string format: ${dateString}. Expected YYYY-MM-DD or YYYY-MM-DD HH:MM.`);
    return null;
  }

  handleKeydown(event) {
    if (event.key === "Delete" || event.key === "Backspace") {
      this.datepickerInstance?.clear();
      this.element.value = "";
    }
  }

  clearSelection() {
    if (this.datepickerInstance) {
      this.datepickerInstance.clear();
    }

    this.element.value = "";
    this.triggerChangeEvent();
    this.clearActivePreset();
  }

  syncFromInlineCalendar(event) {
    const inlineInput = event.target;

    if (inlineInput.value) {
      this.element.value = inlineInput.value;
      this.triggerChangeEvent();
    }
  }

  triggerChangeEvent() {
    this.element.dispatchEvent(new Event("change", { bubbles: true }));
  }

  updateWeekDisplay() {
    if (!this.datepickerInstance) return;

    const selectedDates = this.datepickerInstance.selectedDates;

    if (selectedDates.length > 0) {
      const initialDate = selectedDates[0];
      const weekNumber = this.getWeekNumber(initialDate);
      const year = initialDate.getFullYear();
      this.element.value = `${year}-W${weekNumber.toString().padStart(2, "0")}`;
    } else {
      this.element.value = "";
    }

    this.triggerChangeEvent();
  }

  cleanup() {
    if (this.datepickerInstance) {
      this.datepickerInstance.destroy();
      this.datepickerInstance = null;
    }

    this.cleanupPositioning();
  }

  cleanupPositioning() {
    [this.cleanupAutoUpdate, this.currentPositionCleanupAutoUpdate].forEach((cleanup) => {
      if (cleanup) cleanup();
    });

    this.cleanupAutoUpdate = null;
    this.currentPositionCleanupAutoUpdate = null;
  }

  get availableLocales() {
    return {
      fr: localeFr,
      en: localeEn
    }
  }

  get availableTimeFormat() {
    return {
      fr: 'HH:mm',
      en: 'HH:mm'
    }
  }
}
